import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../../api/axios";
import "./EmailsPage.css";

import AtivarEmail from "./popups/AtivarEmail.jsx";
import InativarEmail from "./popups/InativarEmail.jsx";
import ExcluirEmail from "./popups/ExcluirEmail.jsx";
import CriarEmail from "./popups/CriarEmail.jsx";
import EditarEmail from "./popups/EditarEmail.jsx";

function IconEdit(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function IconTrash(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function ToggleSwitch({ on = false, onToggle, title }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      className={`eml-toggle ${on ? "on" : "off"}`}
      onClick={onToggle}
      title={title || (on ? "Inativar" : "Ativar")}
    >
      <span className="knob" />
    </button>
  );
}

function getEmailGroups(row) {
  if (!row) return [];

  const raw = Array.isArray(row.grupos) && row.grupos.length
    ? row.grupos
    : String(row.grupo || "").split(",");

  const out = [];
  const seen = new Set();

  for (const item of raw) {
    const name = String(item || "").trim();
    if (!name) continue;

    const key = name.toLowerCase();
    if (seen.has(key)) continue;

    seen.add(key);
    out.push(name);
  }

  return out;
}

const UP = (v) => String(v || "").trim().toUpperCase();

function sortRows(a, b) {
  const na = String(a?.nome || a?.apelido || a?.email || "");
  const nb = String(b?.nome || b?.apelido || b?.email || "");
  return na.localeCompare(nb, "pt-BR", { sensitivity: "base" });
}

function rowMatchesStatus(row, status) {
  if (status === "ativos") return Number(row?.ativo) === 1;
  if (status === "inativos") return Number(row?.ativo) === 0;
  return true;
}

export default function EmailsPage({ onBack }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("todos");
  const [grupoFiltro, setGrupoFiltro] = useState(null);

  const [openCriar, setOpenCriar] = useState(false);
  const [editarRow, setEditarRow] = useState(null);
  const [ativarRow, setAtivarRow] = useState(null);
  const [inativarRow, setInativarRow] = useState(null);
  const [excluirRow, setExcluirRow] = useState(null);

  useEffect(() => {
    document.body.classList.add("emails-skin");
    return () => document.body.classList.remove("emails-skin");
  }, []);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const params = {};
      if (q) params.q = q;
      if (status === "ativos") params.ativo = 1;
      if (status === "inativos") params.ativo = 0;

      const r = await api.get("/emails", { params });
      const data = Array.isArray(r.data?.data) ? r.data.data : [];
      setRows([...data].sort(sortRows));
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || "Erro ao listar");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [q, status]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const stats = useMemo(() => {
    const total = rows.length;
    const ativos = rows.filter((r) => Number(r.ativo) === 1).length;
    const inativos = total - ativos;

    const porGrupo = {};
    for (const row of rows) {
      const groups = getEmailGroups(row);
      if (!groups.length) {
        porGrupo["SEM GRUPO"] = (porGrupo["SEM GRUPO"] || 0) + 1;
        continue;
      }
      for (const g of groups) {
        const key = UP(g);
        porGrupo[key] = (porGrupo[key] || 0) + 1;
      }
    }

    const chips = Object.entries(porGrupo).sort((a, b) =>
      a[0].localeCompare(b[0], "pt-BR", { sensitivity: "base" })
    );

    return { total, ativos, inativos, chips };
  }, [rows]);

  const visiveis = useMemo(() => {
    if (!grupoFiltro) return rows;

    return rows.filter((row) => {
      const groups = getEmailGroups(row);
      if (!groups.length) return UP(grupoFiltro) === "SEM GRUPO";
      return groups.some((g) => UP(g) === UP(grupoFiltro));
    });
  }, [rows, grupoFiltro]);

  function goHome() {
    if (onBack) onBack();
    else if (window.history.length > 1) window.history.back();
    else window.location.href = "/";
  }

  function pedirToggle(row) {
    if (Number(row.ativo)) setInativarRow(row);
    else setAtivarRow(row);
  }

  function renderColGroup() {
    return (
      <colgroup>
        <col style={{ width: "17%" }} />
        <col style={{ width: "10%" }} />
        <col style={{ width: "22%" }} />
        <col style={{ width: "15%" }} />
        <col style={{ width: "11%" }} />
        <col style={{ width: "15%" }} />
        <col style={{ width: "10%" }} />
      </colgroup>
    );
  }

  async function salvarCriar(payload) {
    try {
      await api.post("/emails", payload);
      setOpenCriar(false);
      await carregar();
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao cadastrar");
    }
  }

  async function salvarEditar(payload) {
    try {
      const { id, ...body } = payload;
      await api.put(`/emails/${id}`, body);
      setEditarRow(null);
      await carregar();
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao editar");
    }
  }

  async function confirmarAtivar() {
    const row = ativarRow;
    if (!row) return;

    try {
      await api.patch(`/emails/alterar-ativo/${row.id}`, { ativo: 1 });
      setAtivarRow(null);

      if (status !== "todos" && !rowMatchesStatus({ ...row, ativo: 1 }, status)) {
        setRows((prev) => prev.filter((r) => r.id !== row.id));
      } else {
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ativo: 1 } : r)));
      }
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao ativar");
    }
  }

  async function confirmarInativar() {
    const row = inativarRow;
    if (!row) return;

    try {
      await api.patch(`/emails/alterar-ativo/${row.id}`, { ativo: 0 });
      setInativarRow(null);

      if (status !== "todos" && !rowMatchesStatus({ ...row, ativo: 0 }, status)) {
        setRows((prev) => prev.filter((r) => r.id !== row.id));
      } else {
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ativo: 0 } : r)));
      }
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao inativar");
    }
  }

  async function confirmarExcluir() {
    const row = excluirRow;
    if (!row) return;

    try {
      await api.delete(`/emails/${row.id}`);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setExcluirRow(null);
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao excluir");
    }
  }

  return (
    <div className="eml-shell">
      <div className="eml-topbar">
        <button className="eml-btn-hero left" type="button" onClick={goHome}>
          Voltar para o início
        </button>

        <h1 className="eml-title">Catálogo de E-mails</h1>

        <button className="eml-btn-hero right" type="button" onClick={() => setOpenCriar(true)}>
          Cadastrar
        </button>
      </div>

      <div className="eml-head">
        <div className="eml-cards-row eml-cards--all">
          <div className="eml-filters-panel">
            <div className="eml-filters-panel__label">PESQUISA</div>
            <div className="eml-filters-grid">
              <input
                className="eml-search"
                placeholder="Buscar por nome, e-mail, apelido, grupo..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />

              <select className="eml-filter" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="todos">Todos</option>
                <option value="ativos">Ativos</option>
                <option value="inativos">Inativos</option>
              </select>
            </div>
          </div>

          <div className="eml-card">
            <div className="eml-card__label">TOTAL</div>
            <div className="eml-card__value">{stats.total}</div>
          </div>

          <div className="eml-card eml-card--ok">
            <div className="eml-card__label">ATIVOS</div>
            <div className="eml-card__value">{stats.ativos}</div>
          </div>

          <div className="eml-card eml-card--alert">
            <div className="eml-card__label">INATIVOS</div>
            <div className="eml-card__value">{stats.inativos}</div>
          </div>

          <div className="eml-chips-panel">
            <div className="eml-chips-panel__label">POR GRUPO</div>
            <div className="eml-chip-list">
              {stats.chips.map(([g, qnt]) => (
                <button
                  key={g}
                  className={`eml-chip ${grupoFiltro && UP(grupoFiltro) === UP(g) ? "active" : ""}`}
                  type="button"
                  onClick={() => setGrupoFiltro((prev) => (prev && UP(prev) === UP(g) ? null : g))}
                  title={`Filtrar por ${g}`}
                >
                  {g}
                  <span className="eml-chip__num">{qnt}</span>
                </button>
              ))}

              {grupoFiltro && (
                <button
                  className="eml-chip eml-chip--clear"
                  type="button"
                  onClick={() => setGrupoFiltro(null)}
                  title="Limpar filtro"
                >
                  Limpar filtro x
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="eml-wrap">
        {loading ? (
          <div className="eml-empty">Carregando...</div>
        ) : err ? (
          <div className="eml-empty">{err}</div>
        ) : visiveis.length === 0 ? (
          <div className="eml-empty">Nenhum e-mail encontrado.</div>
        ) : (
          <div className="eml-table-wrap">
            <table className="eml-table eml-table--head">
              {renderColGroup()}
              <thead>
                <tr>
                  <th>NOME</th>
                  <th>APELIDO</th>
                  <th>E-MAIL</th>
                  <th>GRUPOS</th>
                  <th>TELEFONE</th>
                  <th>OBSERVAÇÃO</th>
                  <th>CONTROLES</th>
                </tr>
              </thead>
            </table>

            <div className="eml-table-body-scroll">
              <table className="eml-table eml-table--body">
                {renderColGroup()}
                <tbody>
                  {visiveis.map((row) => {
                    const groups = getEmailGroups(row);

                    return (
                      <tr key={row.id}>
                        <td className="eml-td eml-td-strong">{row.nome || "-"}</td>
                        <td className="eml-td">{row.apelido || "-"}</td>
                        <td className="eml-td eml-td-left">{row.email || "-"}</td>
                        <td className="eml-td eml-td-left">
                          <div className="eml-tchips">
                            {groups.length ? (
                              groups.map((grupo) => (
                                <span key={`${row.id}-${grupo}`} className="eml-tchip">
                                  {grupo}
                                </span>
                              ))
                            ) : (
                              <span className="eml-tchip eml-tchip--empty">Sem grupo</span>
                            )}
                          </div>
                        </td>
                        <td className="eml-td">{row.telefone || "-"}</td>
                        <td className="eml-td eml-td-left eml-td-observacao">{row.observacao || "-"}</td>
                        <td className="eml-td eml-td-controls">
                          <div className="eml-actions eml-actions--cluster">
                            <ToggleSwitch
                              on={!!Number(row.ativo)}
                              onToggle={() => pedirToggle(row)}
                              title={Number(row.ativo) ? "Inativar" : "Ativar"}
                            />
                            <span className="eml-actions-divider" aria-hidden="true" />
                            <button className="eml-icon-btn" title="Editar" onClick={() => setEditarRow(row)}>
                              <IconEdit />
                            </button>
                            <span className="eml-actions-divider" aria-hidden="true" />
                            <button className="eml-icon-btn" title="Excluir" onClick={() => setExcluirRow(row)}>
                              <IconTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {openCriar && <CriarEmail open onClose={() => setOpenCriar(false)} onConfirm={salvarCriar} />}

      {editarRow && (
        <EditarEmail
          open
          data={editarRow}
          onClose={() => setEditarRow(null)}
          onConfirm={salvarEditar}
        />
      )}

      {ativarRow && (
        <AtivarEmail
          open
          titulo={ativarRow.apelido || ativarRow.nome || ativarRow.email}
          onClose={() => setAtivarRow(null)}
          onConfirm={confirmarAtivar}
        />
      )}

      {inativarRow && (
        <InativarEmail
          open
          titulo={inativarRow.apelido || inativarRow.nome || inativarRow.email}
          onClose={() => setInativarRow(null)}
          onConfirm={confirmarInativar}
        />
      )}

      {excluirRow && (
        <ExcluirEmail
          open
          titulo={excluirRow.apelido || excluirRow.nome || excluirRow.email}
          onClose={() => setExcluirRow(null)}
          onConfirm={confirmarExcluir}
        />
      )}
    </div>
  );
}
